/**
 * Permissões por tela.
 *
 * Substitui o modelo antigo de dois booleanos (`is_admin`, `catalog_access`) por
 * uma **lista de telas liberadas**, para o administrador escolher exatamente o
 * que cada usuário acessa.
 *
 * Duas regras de projeto:
 *
 * 1. **O catálogo de telas é derivado de `lib/telas.ts`**, o mesmo que alimenta a
 *    sidebar. Assim permissão e menu nunca divergem: tela nova aparece
 *    automaticamente na lista de permissões, sem cadastrar em dois lugares.
 *
 * 2. **`is_admin` continua sendo a chave-mestra.** Administrador acessa tudo, sem
 *    lista. Isso evita o cenário de um admin sem permissão para gerenciar
 *    usuários e o sistema ficar inacessível.
 *
 * ⚠️ **Isto é experiência de uso, não segurança.** Esconder um item de menu não
 * impede um `curl` no endpoint. O backend precisa validar as permissões em cada
 * requisição — ver a seção "O que o backend precisa" em REDESIGN_PERMISSOES.md.
 */

// Extensão explícita: permite que os testes importem este módulo direto no Node,
// sem build nem resolução de alias. `allowImportingTsExtensions` está ligado.
import { SECOES } from './telas.ts'

/** Uma tela permissionável. A chave é a rota — estável e já única. */
export interface TelaPermissao {
  /** Chave da permissão. É a rota, ex.: `/importacao`. */
  chave: string
  label: string
  /** Módulo a que pertence, para agrupar na interface. */
  modulo: string
  /** Só administrador acessa; não aparece na lista de escolha. */
  adminOnly?: boolean
}

/** Catálogo de telas, derivado de `lib/telas.ts`. */
export const TELAS: TelaPermissao[] = SECOES.flatMap((secao) => {
  // Seção sem filhos é ela mesma uma tela.
  if (!secao.children?.length) {
    return [
      {
        chave: secao.to,
        label: secao.label,
        modulo: secao.label,
        adminOnly: secao.adminOnly,
      },
    ]
  }
  // Com filhos, cada filho é uma tela do módulo.
  return secao.children.map((filho) => ({
    chave: filho.to,
    label: filho.label,
    modulo: secao.label,
    adminOnly: secao.adminOnly,
  }))
})

/** Telas que o admin pode conceder (exclui as exclusivas de administrador). */
export const TELAS_CONCEDIVEIS = TELAS.filter((t) => !t.adminOnly)

/** Módulos na ordem da navegação, com suas telas. */
export const MODULOS_PERMISSAO: Array<{ modulo: string; telas: TelaPermissao[] }> = (() => {
  const mapa = new Map<string, TelaPermissao[]>()
  for (const t of TELAS_CONCEDIVEIS) {
    const atual = mapa.get(t.modulo)
    if (atual) atual.push(t)
    else mapa.set(t.modulo, [t])
  }
  return [...mapa.entries()].map(([modulo, telas]) => ({ modulo, telas }))
})()

/**
 * Perfis prontos.
 *
 * Motivo de existir: criar um usuário marcando 18 caixas uma por uma é trabalhoso
 * e propenso a esquecimento. O administrador aplica um perfil e ajusta o que
 * precisar. Não é um papel fixo no banco — é só um atalho de preenchimento.
 */
export interface Perfil {
  id: string
  label: string
  descricao: string
  /** `'*'` significa todas as telas concedíveis. */
  telas: string[] | '*'
}

export const PERFIS: Perfil[] = [
  {
    id: 'completo',
    label: 'Acesso completo',
    descricao: 'Todas as telas, exceto o painel administrativo.',
    telas: '*',
  },
  {
    id: 'operacional',
    label: 'Operacional',
    descricao: 'Processos, DUIMP, LPCO e empresas. Sem financeiro nem configurações.',
    telas: [
      '/dashboard',
      '/importacao',
      '/estimativas',
      '/catalog',
      '/lpco/new',
      '/lpco/anexar',
      '/lpco/painel',
      '/batch',
      '/history',
      '/mappings',
      '/duimp',
      '/duimp/retificacao',
      '/exportacao',
      '/agenciamento',
      '/credenciamento',
      '/credenciamento/alertas',
      '/empresas',
      '/awb',
    ],
  },
  {
    id: 'despachante',
    label: 'Despachante',
    descricao: 'Foco em DUIMP, LPCO e processos de importação.',
    telas: [
      '/dashboard',
      '/importacao',
      '/catalog',
      '/lpco/new',
      '/lpco/anexar',
      '/lpco/painel',
      '/history',
      '/duimp',
      '/duimp/retificacao',
      '/empresas',
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    descricao: 'Financeiro, estimativas e consulta de processos.',
    telas: ['/dashboard', '/financeiro', '/estimativas', '/importacao', '/empresas'],
  },
  {
    id: 'consulta',
    label: 'Somente consulta',
    descricao: 'Dashboard e listas de processos, sem cadastros.',
    telas: ['/dashboard', '/importacao', '/exportacao', '/agenciamento', '/credenciamento'],
  },
]

/** Expande um perfil na lista concreta de chaves. */
export function telasDoPerfil(perfil: Perfil): string[] {
  return perfil.telas === '*' ? TELAS_CONCEDIVEIS.map((t) => t.chave) : perfil.telas
}

/** Identifica se a seleção corresponde exatamente a algum perfil pronto. */
export function perfilDaSelecao(selecionadas: Set<string>): Perfil | null {
  for (const p of PERFIS) {
    const alvo = telasDoPerfil(p)
    if (alvo.length === selecionadas.size && alvo.every((t) => selecionadas.has(t))) return p
  }
  return null
}

/* -------------------------------------------------------------- verificação */

/** O que o app sabe sobre o usuário logado. */
export interface UsuarioSessao {
  is_admin?: number | boolean
  /** Lista de rotas liberadas. `undefined` = backend antigo, sem permissões. */
  permissoes?: string[] | null
}

const ehVerdade = (v: unknown) => v === 1 || v === true

/** Conjunto das rotas que são chaves de permissão próprias. */
const CHAVES = new Set(TELAS.map((t) => t.chave))

/**
 * O usuário pode acessar esta tela?
 *
 * **Regra de herança, e por que não é simples prefixo:**
 *
 * `/importacao/62` deve herdar de `/importacao` — é a tela de detalhe do mesmo
 * processo. Mas `/duimp/retificacao` **não** deve herdar de `/duimp`: são duas
 * permissões distintas no catálogo, e retificar altera uma declaração já
 * registrada no Portal. Herdar por prefixo daria acesso à retificação a quem só
 * recebeu a DUIMP.
 *
 * Então: **rota que é chave própria exige permissão explícita**; rota que não
 * está no catálogo (detalhe, formulário) herda da chave-pai mais próxima.
 *
 * Compatibilidade: se `permissoes` não vier (backend ainda sem o campo), libera
 * tudo. Assim a migração não tranca ninguém fora — o aperto vem quando o backend
 * passar a enviar a lista.
 */
export function podeAcessar(usuario: UsuarioSessao | null, rota: string): boolean {
  if (!usuario) return false
  if (ehVerdade(usuario.is_admin)) return true
  if (usuario.permissoes == null) return true

  const liberadas = usuario.permissoes

  // Rota com chave própria: precisa estar liberada nominalmente.
  if (CHAVES.has(rota)) return liberadas.includes(rota)

  // Rota sem chave própria: herda do ancestral mais específico que seja chave.
  const partes = rota.split('/').filter(Boolean)
  for (let i = partes.length - 1; i >= 1; i--) {
    const ancestral = `/${partes.slice(0, i).join('/')}`
    if (CHAVES.has(ancestral)) return liberadas.includes(ancestral)
  }
  return false
}

/** Primeira rota que o usuário pode abrir — para onde mandar após o login. */
export function rotaInicial(usuario: UsuarioSessao | null): string {
  if (podeAcessar(usuario, '/dashboard')) return '/dashboard'
  const primeira = TELAS_CONCEDIVEIS.find((t) => podeAcessar(usuario, t.chave))
  return primeira?.chave ?? '/login'
}
